import React, { useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Divider
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axiosClient from '../../../../../../lib/axios';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { formatarValorMonetario } from '../../../../../../helpers/masks';

const CardInformacoesPrincipais: React.FC<CardInformacoesPrincipaisProps> = ({
  ativo,
  codigo,
  nome,
  imagemUrl,
  produtoId,
  onImagemChange,
  unidadeMedida,
  estoqueAtual,
  custoMedio,
  precoVenda,
  estoqueDisponivelVendas,
  comprasAReceber,
  pedidosAEntregar,
  disponivelTotal,
  contadores = true
}) => {
  const [imagemState, setImagemState] = useState<{
    arquivo: File | null;
    preview: string | null;
    editando: boolean;
  }>({
    arquivo: null,
    preview: null,
    editando: false
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const temImagem = !!imagemUrl;
  const imagemExibicao = imagemUrl || null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Por favor, selecione um arquivo de imagem válido', { variant: 'error' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagemState(prev => ({
          ...prev,
          arquivo: file,
          preview: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSalvarImagem = async () => {
    if (!imagemState.arquivo) return;

    try {
      const formData = new FormData();
      formData.append('produto_id', produtoId.toString());
      formData.append('imagem', imagemState.arquivo);

      const response = await axiosClient.post('/estoque/produtos/produto-imagem', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (onImagemChange) onImagemChange(response.data.path_imagem);

      setImagemState({
        arquivo: null,
        preview: null,
        editando: false
      });

      enqueueSnackbar('Imagem salva com sucesso', { variant: 'success' });
      queryClient.invalidateQueries('detalhe-produto');
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      enqueueSnackbar('Erro ao salvar a imagem', { variant: 'error' });
    }
  };

  const handleCancelar = () => {
    setImagemState({
      arquivo: null,
      preview: null,
      editando: false
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleIniciarUpload = () => {
    setImagemState(prev => ({ ...prev, editando: true }));
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Card sx={{ margin: '0 auto' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            Código/Nome
          </Typography>
          <Chip label={ativo ? 'Ativo' : 'Inativo'} color={ativo ? 'success' : 'error'} size="small" />
        </Box>

        <Typography variant="h6" fontWeight="bold" mb={3}>
          {codigo} - {nome}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Estoque atual
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Custo médio
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Preço de venda
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6" fontWeight="bold">
              {estoqueAtual} {unidadeMedida}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatarValorMonetario(custoMedio)}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatarValorMonetario(precoVenda)}
            </Typography>
          </Box>
        </Box>

        {contadores && (
          <>

            <Box>
              {[
                { label: 'Qtd. de compras a receber', value: comprasAReceber },
                { label: 'Pedidos a entregar', value: pedidosAEntregar },
                { label: 'Disponível para vendas', value: estoqueDisponivelVendas },
                { label: 'Disponível total', value: disponivelTotal },
              ].map((item, index) => (
                <Box key={index} display="flex" alignItems="center" py={0.5}>
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {item.label}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      borderBottom: '1px dotted',
                      mx: 1,
                      height: '1px'
                    }}
                  />
                  <Typography variant="body2" fontWeight="medium" sx={{ flexShrink: 0 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
        <Divider sx={{ my: 2 }} />

        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 320,
              height: 220,
              bgcolor: 'grey.200',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: 1
            }}
          >
            {imagemState.preview ? (
              <img
                src={imagemState.preview}
                alt="Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            ) : imagemExibicao ? (
              <img
                src={imagemExibicao}
                alt={nome}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
                onError={() => {
                  enqueueSnackbar('Erro ao carregar a imagem', { variant: 'error' });
                }}
              />
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                width="100%"
                bgcolor="#f5f5f5"
              >
                <AddPhotoAlternateIcon sx={{ fontSize: 60, color: 'grey.500', mb: 1 }} />
                <Typography variant="caption" color="textSecondary" align="center">
                  Nenhuma imagem
                </Typography>
              </Box>
            )}
          </Box>

          {/* Input de arquivo oculto */}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleFileChange}
            ref={fileInputRef}
          />

          {!imagemState.editando ? (
            <Box width="100%" display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleIniciarUpload}
              >
                <CloudUploadIcon />
              </Button>
            </Box>
          ) : (
            <Box display="flex" gap={2} alignItems="center">
              <Button
                variant="contained"
                onClick={handleSalvarImagem}
                disabled={!imagemState.arquivo}
                size="small"
              >
                Salvar
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelar}
                size="small"
              >
                Cancelar
              </Button>
            </Box>
          )}
        </Box>

      </CardContent>
    </Card>
  );
};

export default CardInformacoesPrincipais;

export interface CardInformacoesPrincipaisProps {
  ativo: boolean;
  codigo: number | string;
  nome: string;
  unidadeMedida: string
  estoqueAtual: number
  custoMedio: number
  estoqueDisponivelVendas: number
  comprasAReceber: number
  pedidosAEntregar: number
  disponivelTotal: number
  contadores?: boolean
  precoVenda: number;
  onSuccess?: () => void;
  imagemUrl?: string;
  produtoId: number;
  onImagemChange?: (url: string) => void;
}